/**
 * frontend/src/api/supportCases.js
 * ---------------------------------------------------------------------------
 * Client for the support case API, base path /api/support-cases.
 *
 * Every call goes through `dashboardApi` -- the configured axios instance
 * exported from ./index.js -- exactly like api/organizerRbac.js and
 * api/ticketingProviders.js do. Bare `axios` carries neither the Bearer token
 * nor the active-country header, and its 401s never reach the silent-refresh
 * interceptor, which is what __tests__/noBareAxiosInActions.test.js exists to
 * prevent. There is no second client here on purpose.
 *
 * These functions return the raw axios promise, so a caller reads
 * `response.data` and an error's `error.response.data.message` and
 * `error.response.data.code`, the same error shape the rest of the dashboard
 * already handles. Nothing here decides permissions, transitions, SLA or
 * priority: the server is authoritative and these are transport only.
 *
 * "case", never "ticket": `MessagingTicket` and event box-office ticketing are
 * both different things in this repo.
 */
import { dashboardApi as API } from "./index.js";

const BASE = "/api/support-cases";

const caseBase = (caseId) => `${BASE}/${encodeURIComponent(caseId)}`;

// --- Vocabulary and read paths -------------------------------------------

/**
 * The live category, status and priority vocabularies. No screen may hardcode
 * an enum list; utilities/supportCaseVocabulary.js layers this over its shipped
 * fallback so a failed call still leaves the filters usable.
 */
export const apiSupportCaseOptions = () => API.get(`${BASE}/meta/options`);

/**
 * params: status, category, priority, assigneeType, assignedTo, vendorId,
 * orderId, breached, q, from, to, page, limit, sort.
 * Build them with buildCaseListQuery so nothing outside the contract is sent.
 * Results are scoped by the server: a vendor manager and a staff member call
 * the same path and receive different rows.
 */
export const apiListSupportCases = (params = {}) => API.get(BASE, { params });

/** Counts by status and by priority plus the SLA breach count, caller-scoped. */
export const apiSupportCaseSummary = (params = {}) =>
  API.get(`${BASE}/stats/summary`, { params });

export const apiGetSupportCase = (caseId) => API.get(caseBase(caseId));

// --- Create ---------------------------------------------------------------

/** body: { subject, description, category, orderId?, vendorId?, evidence?: [{ url, type, caption }] } */
export const apiCreateSupportCase = (body) => API.post(BASE, body);

// --- Thread and evidence -------------------------------------------------

/**
 * body: { body, isInternal?, attachments?: [{ url, type, name }] }
 * `isInternal: true` is an internal note. The server accepts it only from a
 * vendor or staff caller and refuses it otherwise, so the composer flags it
 * explicitly rather than inferring it from who is signed in.
 */
export const apiPostSupportCaseMessage = (caseId, body) =>
  API.post(`${caseBase(caseId)}/messages`, body);

/** body: { evidence: [{ url, type, caption }] } */
export const apiAddSupportCaseEvidence = (caseId, body) =>
  API.post(`${caseBase(caseId)}/evidence`, body);

// --- Handling -------------------------------------------------------------

/** body: { status, note? }. The server owns the transition matrix. */
export const apiUpdateSupportCaseStatus = (caseId, body) =>
  API.patch(`${caseBase(caseId)}/status`, body);

/** body: { assigneeType, assignee?, team? } */
export const apiAssignSupportCase = (caseId, body) =>
  API.patch(`${caseBase(caseId)}/assign`, body);

/** body: { reason? } */
export const apiEscalateSupportCase = (caseId, body = {}) =>
  API.post(`${caseBase(caseId)}/escalate`, body);

/**
 * body: { outcome, summary?, financialImpact?: { currency, amount, refundId } }
 */
export const apiResolveSupportCase = (caseId, body) =>
  API.post(`${caseBase(caseId)}/resolve`, body);

/** body: { reason? } */
export const apiReopenSupportCase = (caseId, body = {}) =>
  API.post(`${caseBase(caseId)}/reopen`, body);

/** body: { rating, comment? }. Raiser only, and only once resolved or closed. */
export const apiRateSupportCase = (caseId, body) =>
  API.post(`${caseBase(caseId)}/satisfaction`, body);

/**
 * The one place that turns any failure from these calls into a sentence. Reads
 * the repo's standard error envelope ({ message, code }) and falls back through
 * the axios message so a network drop is never reported as "undefined".
 */
export const supportCaseErrorMessage = (error, fallback = "Something went wrong.") =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

export default apiListSupportCases;
